import { useState } from "react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { useTranslation } from "@repo/core";

// Character-count threshold rather than DOM/line measurement - simpler and
// avoids a ResizeObserver for what only needs to roughly cap the strip at a
// few lines.
const TRUNCATE_LENGTH = 280;

const NoteStrip = ({
  note,
  canEdit,
  onEditClick,
  onDeleteClick,
}: {
  note: string;
  canEdit: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
}) => {
  const { tStrategy, tCommon } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const isTruncated = !isExpanded && note.length > TRUNCATE_LENGTH;
  const displayedNote = isTruncated
    ? `${note.slice(0, TRUNCATE_LENGTH)}…`
    : note;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {tStrategy("note.stripTitle")}
        </span>
        {canEdit && (
          <div className="flex items-center gap-3">
            <button
              onClick={onEditClick}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
            >
              <HiPencil size={14} />
              <span className="text-xs font-medium">{tCommon("edit")}</span>
            </button>
            <button
              onClick={onDeleteClick}
              className="flex items-center gap-1 text-red-600 hover:text-red-800 transition-colors cursor-pointer"
            >
              <HiTrash size={14} />
              <span className="text-xs font-medium">{tCommon("delete")}</span>
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
        {displayedNote}
        {isTruncated && (
          <>
            {" "}
            <button
              onClick={() => setIsExpanded(true)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer"
            >
              {tStrategy("note.more")}
            </button>
          </>
        )}
      </p>
      {!isTruncated && note.length > TRUNCATE_LENGTH && (
        <button
          onClick={() => setIsExpanded(false)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer mt-1"
        >
          {tStrategy("showLess")}
        </button>
      )}
    </div>
  );
};

export default NoteStrip;
